import { get, set, del, createStore, keys, getMany, setMany } from 'idb-keyval';
import { Transaction, ColumnMapping } from '../types';

const transactionStore = createStore('transactions', 'transactions');
const mappingStore = createStore('mappings', 'mappings');

const BATCH_SIZE = 5000; // Increased batch size for better performance

export const storage = {
  // Transaction operations
  async saveTransactions(transactions: Transaction[]): Promise<void> {
    try {
      // Clear existing transactions first
      await this.clearTransactions();

      // Prepare batches for bulk operation
      const batches: [string, Transaction[]][] = [];
      for (let i = 0; i < transactions.length; i += BATCH_SIZE) {
        const batch = transactions.slice(i, i + BATCH_SIZE);
        batches.push([`transactions_${i}`, batch]);
      }

      // Save all batches in parallel
      await Promise.all([
        setMany(batches, transactionStore),
        set('transactions_metadata', {
          count: transactions.length,
          lastUpdated: new Date().toISOString(),
          batchCount: Math.ceil(transactions.length / BATCH_SIZE)
        }, transactionStore)
      ]);
    } catch (error) {
      console.error('Error saving transactions:', error);
      throw error;
    }
  },

  async getTransactions(): Promise<Transaction[]> {
    try {
      const metadata = await get('transactions_metadata', transactionStore);
      if (!metadata) return [];

      // Get all transaction keys
      const allKeys = await keys(transactionStore);
      const transactionKeys = allKeys.filter(key => 
        typeof key === 'string' && key.startsWith('transactions_')
      );

      // Fetch all batches in parallel
      const batches = await getMany(transactionKeys, transactionStore);
      
      // Combine all batches
      return batches.flat().filter(Boolean);
    } catch (error) {
      console.error('Error loading transactions:', error);
      return [];
    }
  },

  async clearTransactions(): Promise<void> {
    try {
      const allKeys = await keys(transactionStore);
      const transactionKeys = allKeys.filter(key => 
        typeof key === 'string' && key.startsWith('transactions_')
      );
      
      // Delete all transaction keys in parallel
      await Promise.all([
        del('transactions_metadata', transactionStore),
        ...transactionKeys.map(key => del(key, transactionStore))
      ]);
    } catch (error) {
      console.error('Error clearing transactions:', error);
    }
  },

  // Mapping operations
  async saveMapping(name: string, mapping: ColumnMapping): Promise<void> {
    const mappings = await this.getMappings();
    mappings[name] = mapping;
    await set('mappings', mappings, mappingStore);
  },

  async getMappings(): Promise<Record<string, ColumnMapping>> {
    return (await get('mappings', mappingStore)) || {};
  },

  async clearMappings(): Promise<void> {
    await del('mappings', mappingStore);
  },

  // Export/Import
  async exportData(): Promise<string> {
    try {
      const [transactions, mappings] = await Promise.all([
        this.getTransactions(),
        this.getMappings(),
      ]);
      return JSON.stringify({ transactions, mappings });
    } catch (error) {
      console.error('Error exporting data:', error);
      return '{}';
    }
  },

  async importData(json: string): Promise<void> {
    try {
      const data = JSON.parse(json);
      if (data.transactions) {
        await this.saveTransactions(data.transactions);
      }
      if (data.mappings) {
        await set('mappings', data.mappings, mappingStore);
      }
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  },
}; 